import { Controller } from "@hotwired/stimulus";
import SlimSelect from "slim-select";

export default class ProductSelectController extends Controller {
  static targets = ["productSelect", "unitValue", "suggestedValue"];

  connect() {
    const elementOptions = JSON.parse(
      this.productSelectTarget.dataset?.slimOptions || "{}",
    );

    this.selectedProductOption = this.getSelectedOptionFromDom();

    this.productSelect = new SlimSelect({
      select: this.productSelectTarget,
      settings: {
        placeholderText: "Escolha...",
        searchText: "Nenhum resultado...",
        searchPlaceholder: "Pesquise...",
        allowDeselect: true,
      },
      events: {
        search: (search) => this.searchProducts(search),
        afterChange: (products) => {
          const product = Array.isArray(products) ? products[0] : null;
          this.selectedProductOption = this.normalizeSelectedOption(product);

          if (!product) return;

          if (this.hasUnitValueTarget) {
            this.unitValueTarget.value = Number(
              this.resolveProductUnitValue(product),
            ).toFixed(1);
          }
        },
      },
      ...elementOptions,
    });
  }

  disconnect() {
    if (this.productSelectTimeout) {
      clearTimeout(this.productSelectTimeout);
      this.productSelectTimeout = null;
    }

    if (this.productSelect) {
      this.productSelect.destroy();
      this.productSelect = null;
    }
  }

  async searchProducts(search) {
    if (this.productSelectTimeout) clearTimeout(this.productSelectTimeout);

    return await new Promise((resolve) => {
      this.productSelectTimeout = setTimeout(async () => {
        const urlString = "/admin/products/slim_select_search";
        const url = new URL(urlString, document.baseURI);
        url.searchParams.append("search", search);

        try {
          const response = await fetch(url);
          const data = await response.json();
          const normalizedData = this.normalizeProductOptions(data);
          resolve(this.mergeSelectedOption(normalizedData));
        } catch (error) {
          resolve([]);
        }
      }, 300);
    });
  }

  normalizeProductOptions(data) {
    if (!Array.isArray(data)) return data;

    return data.map((option) => {
      if (!option || typeof option !== "object") return option;

      const optionId =
        option.product_id ?? option.id ?? option.value ?? option.unit_value;
      const optionUnitValue =
        option.unit_value ?? option.price ?? option.value ?? 0;

      return {
        ...option,
        value: optionId,
        unit_value: optionUnitValue,
        data: {
          ...(option.data || {}),
          unit_value: option.data?.unit_value ?? optionUnitValue,
        },
      };
    });
  }

  mergeSelectedOption(data) {
    if (!Array.isArray(data)) return data;

    const selectedOption = this.getSelectedOption();

    if (!selectedOption || selectedOption.value === "") {
      return data;
    }

    const selectedValue = String(selectedOption.value);
    const selectedText = selectedOption.text || "";

    const hasSelectedOption = data.some(
      (option) => String(option.value) === selectedValue,
    );

    if (hasSelectedOption) {
      return data.map((option) => {
        if (String(option.value) !== selectedValue) return option;

        return {
          ...option,
          text: option.text || selectedText,
          unit_value: option.unit_value ?? selectedOption.unit_value,
          data: {
            ...(option.data || {}),
            unit_value:
              option.data?.unit_value ??
              selectedOption.data?.unit_value ??
              selectedOption.unit_value,
          },
          selected: true,
        };
      });
    }

    return [{ ...selectedOption, selected: true }, ...data];
  }

  getSelectedOption() {
    const domOption = this.getSelectedOptionFromDom();

    if (domOption && domOption.value !== "") {
      return domOption;
    }

    return this.selectedProductOption;
  }

  getSelectedOptionFromDom() {
    const selectedOption = this.productSelectTarget.selectedOptions?.[0];

    if (!selectedOption) return null;

    return {
      value: selectedOption.value,
      text: selectedOption.text,
      unit_value: selectedOption.dataset?.unitValue,
      data: {
        unit_value: selectedOption.dataset?.unitValue,
      },
    };
  }

  normalizeSelectedOption(product) {
    if (!product || product.value === undefined || product.value === null) {
      return null;
    }

    return {
      value: product.value,
      text: product.text || "",
      unit_value: this.resolveProductUnitValue(product),
      data: {
        ...(product.data || {}),
        unit_value: this.resolveProductUnitValue(product),
      },
    };
  }

  resolveProductUnitValue(product) {
    if (!product) return 0;

    return product.data?.unit_value ?? product.unit_value ?? product.value ?? 0;
  }
}
